import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInYears } from 'date-fns';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import BusinessIcon from '@mui/icons-material/Business';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import ImageIcon from '@mui/icons-material/Image';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { BooleanFilter } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { FragmentType, ThirdPartyCategoryId } from '@e-socna/data-layer';

import { formatAmount } from '../../utils/format-amount.util';
import { formatDate } from '../../utils/format-date.util';
import {
  formatOrganizationIdentifierValue,
  getOrganizationIdentifierLabel,
} from '../../utils/format-organization-identifier.util';
import { Memo } from '../../components/Memo';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import {
  PATH_ORGANIZATION_GENERAL,
  PATH_ORGANIZATION_GENERAL_EDIT,
} from '../../app/constants/paths.constant';
import { OrganizationForm } from '../../forms/ThirdParty/Organization';
import { useNafEntriesQuery } from '../../forms/ThirdParty/hooks/useNafEntriesQuery.hook';
import { useLegalStatusesQuery } from '../../forms/ThirdParty/hooks/useLegalStatusesQuery.hook';
import { RightDrawer } from '../../components/RightDrawer';
import { SpeedDial } from '../../components/SpeedDial';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { FormVariant } from '../../forms';
import { Avatar } from '../../components/Avatar';
import { thirdPartyTooltipOf } from '../../utils/third-party-tooltip.util';
import { Code } from '../../components/Code';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const OrganizationPage_GeneralInfo_Organization_Fragment = graphql(`
  fragment OrganizationPage_GeneralInfo_Organization_Fragment on Organization {
    _id
    code
    codingConfig {
      kind
      strategyId
      ... on CodingConfigManual {
        code
      }
      ...CodeCmp_CodingConfig_Fragment
    }
    internalMemo
    memo
    isInternal
    isVip
    identifier {
      kind
      value
    }
    vatNumber
    name
    tradingName
    legalStatusId
    shareCapital {
      value
      currencyId
    }
    nafId
    activity
    birthDate
    logoImageUri
    color
    categoryIds
    ...ThirdPartyForm_Organization_Fragment
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationPage_GeneralInfo_Organization_Fragment
  >;
}

export const GeneralInfo = ({
  organization: pOrganization,
}: Readonly<Props>) => {
  const organization = getFragmentData(
    OrganizationPage_GeneralInfo_Organization_Fragment,
    pOrganization,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_ORGANIZATION_GENERAL,
      edit: PATH_ORGANIZATION_GENERAL_EDIT,
    },
    { id: organization._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const [isLogoDrawerOpen, setIsLogoDrawerOpen] = useState(false);

  const openLogoDrawer = useCallback(() => {
    setIsLogoDrawerOpen(true);
  }, []);

  const closeLogoDrawer = useCallback(() => {
    setIsLogoDrawerOpen(false);
  }, []);

  const { currenciesIdMap, thirdPartyCategoriesIdMap } = useReferencesContext();

  const { data: nafEntriesData } = useNafEntriesQuery();
  const nafEntry = useMemo(
    () =>
      (nafEntriesData?.findAllActiveNafEntries ?? EMPTY_ARRAY).find(
        (entry) => entry._id === organization.nafId,
      ),
    [nafEntriesData, organization.nafId],
  );

  const { data: legalStatusesData } = useLegalStatusesQuery();
  const legalStatusLabel = useMemo(
    () =>
      (legalStatusesData?.findAllActiveLegalStatuses ?? EMPTY_ARRAY).find(
        (legalStatus) => legalStatus._id === organization.legalStatusId,
      )?.label,
    [legalStatusesData, organization.legalStatusId],
  );

  const categories = organization.categoryIds
    ?.map(
      (categoryId) =>
        thirdPartyCategoriesIdMap[categoryId as ThirdPartyCategoryId]?.label,
    )
    .filter(BooleanFilter);

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Memo
          content={{
            internal: organization.internalMemo,
            memo: organization.memo,
          }}
          display={{ internal: true, memo: true }}
        />
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code
              code={organization.code}
              codingConfig={organization.codingConfig}
            />
            {organization.isInternal ? (
              <Typography gutterBottom variant="body1">
                Tiers interne : Oui
              </Typography>
            ) : (
              <Typography gutterBottom variant="body1">
                VIP : {organization.isVip ? 'Oui' : 'Non'}
              </Typography>
            )}
            <Typography gutterBottom variant="body1">
              Raison Sociale : {organization.name}
            </Typography>
            <Typography gutterBottom variant="body1">
              {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
              Nom Commercial : {organization.tradingName || 'Inconnu'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Catégories :{' '}
              {categories?.length ? categories.join(', ') : 'Aucune'}
            </Typography>
            <Typography gutterBottom variant="body1">
              {getOrganizationIdentifierLabel(organization.identifier)} :{' '}
              {formatOrganizationIdentifierValue(organization.identifier) ??
                'Inconnu'}
            </Typography>
            <Typography gutterBottom variant="body1">
              {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
              N° TVA Intracommunautaire : {organization.vatNumber || 'Inconnu'}
            </Typography>
            <Typography gutterBottom variant="body1">
              {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
              Statut Juridique : {legalStatusLabel || 'Inconnu'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Capital Social :{' '}
              {organization.shareCapital
                ? formatAmount(organization.shareCapital, currenciesIdMap)
                : 'Inconnu'}
            </Typography>
            <Typography gutterBottom variant="body1">
              NAF :{' '}
              {nafEntry
                ? `${nafEntry.code} - ${nafEntry.designation}`
                : 'Inconnu'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Activité :{' '}
              {/*eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing*/}
              {organization.activity || 'Inconnue'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Date de fondation :{' '}
              {organization.birthDate
                ? `${formatDate(organization.birthDate)} (${differenceInYears(
                    new Date(),
                    new Date(organization.birthDate),
                  ).toFixed()} ans)`
                : 'Inconnue'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ mr: 1 }} variant="body1">
                Logo :
              </Typography>
              {organization.logoImageUri ? (
                <Button
                  onClick={openLogoDrawer}
                  size="small"
                  startIcon={<ImageIcon />}
                  variant="outlined"
                >
                  Voir le logo
                </Button>
              ) : (
                <Typography variant="body1">Inconnu</Typography>
              )}
            </Box>
            <Avatar
              bgColor={organization.color}
              icon={<BusinessIcon />}
              imageUri={organization.logoImageUri}
              label={thirdPartyTooltipOf(organization.name)}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            />
          </Box>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editOrganizationGeneralInfo',
            icon: <EditIcon />,
            name: 'Modifier Infos Générales',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations Générales de l'Organisation"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <OrganizationForm
          formVariant={FormVariant.Modification}
          initialValues={organization}
          isInternal={organization.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          organizationId={organization._id}
          stepIdsSubset={[ThirdPartyFormTab.GeneralInfo]}
          title="Formulaire de modification des Informations Générales de l'Organisation"
        />
      </RightDrawer>
      {!!organization.logoImageUri && (
        <RightDrawer isOpen={isLogoDrawerOpen} onDrawerClose={closeLogoDrawer}>
          <Box sx={{ flexGrow: 1, p: 2 }}>
            <Typography gutterBottom variant="h6">
              Logo
            </Typography>
            <Box
              component="img"
              src={organization.logoImageUri}
              sx={{ maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </RightDrawer>
      )}
    </>
  );
};
